import React, { useState, useMemo, useEffect, useCallback } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import _ from "lodash";
import {
  CircularProgress,
  Icon,
  IconButton,
  InputAdornment,
  TableSortLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { isJsonFormat } from "../../../../util/UtilFormat";
import useTableSort from "../../../../core/hooks/useTableSort";
import {
  DATE_TIME_PATTERN,
  FRONT_DATE_TIME_PATTERN,
  TOAST_CONTAINER_LAYOUT,
} from "../../../../util/Constants";
import * as UtilExcel from "../../../../util/UtilExcel";
import GetAppIcon from "@material-ui/icons/GetApp";
import { safeFormattingDate } from "../../../../util/UtilDates";

const useStyles = makeStyles({
  root: {
    width: "100%",
    padding: 5,
  },
  toolbarContainer: {
    display: "flex",
    alignItems: "center",
  },
  container: {
    maxHeight: "65vh",
  },
  searchInput: {
    width: "100%",
  },
  cancelSearchBtn: {
    padding: 5,
  },
  endAdornmentCont: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  infoFiltered: {
    color: "lightgrey",
    fontSize: 11,
  },
});

const commonIcon = {
  width: "auto",
  height: "auto",
  padding: 1,
};

const SearchBar = ({
  kpi,
  value,
  handleChange,
  onCancelSearch,
  filteredItemsCount,
  totalItemsCount,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <TextField
      id={`search_table_${kpi?.nombre_kpi}`}
      label={null}
      value={value}
      onChange={handleChange}
      variant="outlined"
      placeholder={t("HEADER_SEARCH_PLACEHOLDER")}
      margin="dense"
      className={classes.searchInput}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <div className={classes.endAdornmentCont}>
              {!_.isNil(filteredItemsCount) &&
                !_.isNil(totalItemsCount) &&
                filteredItemsCount !== totalItemsCount && (
                  <Typography className={classes.infoFiltered}>
                    {`(${filteredItemsCount} / ${totalItemsCount})`}
                  </Typography>
                )}

              <IconButton
                disabled={_.isNil(value) || "" === value}
                onClick={onCancelSearch}
                className={classes.cancelSearchBtn}
              >
                <Icon
                  className={
                    !_.isNil(value) && "" !== value
                      ? "fas fa-times"
                      : "fas fa-search"
                  }
                  style={commonIcon}
                />
              </IconButton>
            </div>
          </InputAdornment>
        ),
      }}
    />
  );
};

const ExportExcelButton = ({ data, columns, fileName }) => {
  const { t } = useTranslation();
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const columnsToExport = useMemo(() => {
    if (columns && columns.length > 0) {
      return columns.map((c) => ({
        name: c.colName,
        value: c.colName,
        type: c.type || "string",
      }));
    }
    return [];
  }, [columns]);

  const dataToExport = useMemo(() => {
    if (!_.isNil(data) && data.length > 0) {
      return data.map((row) => {
        const newRow = {};
        columns.forEach((col) => {
          newRow[col.colName] = row[col.colName];
        });
        return newRow;
      });
    }
    return [];
  }, [data, columns]);

  const handleDownloadExcel = useCallback(async () => {
    setIsDownloadingExcel(true);

    try {
      if (dataToExport?.length > 0 && columnsToExport.length > 0) {
        const sheetName = "Detalle";
        await UtilExcel.downloadExcelUniqueSheet({
          data: dataToExport,
          columns: columnsToExport,
          fileName,
          sheetName,
        });
      } else {
        toast.warn(t("TABLE_EXPORT_EXCEL_ERROR"), {
          containerId: TOAST_CONTAINER_LAYOUT,
        });
      }
    } catch (error) {
      toast.error(t("TABLE_EXPORT_EXCEL_ERROR"), {
        containerId: TOAST_CONTAINER_LAYOUT,
      });
    }

    setTimeout(() => {
      setIsDownloadingExcel(false);
    }, 300);
  }, [dataToExport, columnsToExport, t, fileName]);

  return (
    <IconButton onClick={handleDownloadExcel} disabled={isDownloadingExcel}>
      {isDownloadingExcel ? <CircularProgress size={15} /> : <GetAppIcon />}
    </IconButton>
  );
};

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.content.mainColor,
    color: theme.palette.common.white,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: `${theme.palette.content.mainColor}15`,
    },
  },
}))(TableRow);

export default function KpiTableDetail({ kpi, listDetail, refreshDetailList }) {
  const classes = useStyles();

  const columns = useMemo(() => {
    if (!_.isNil(listDetail) && !_.isEmpty(listDetail?.columns)) {
      return listDetail?.columns;
    } else {
      return [];
    }
  }, [listDetail]);

  const data = useMemo(() => {
    if (isJsonFormat(listDetail?.data)) {
      return JSON.parse(listDetail?.data);
    } else {
      return null;
    }
  }, [listDetail]);

  const originalRows = useMemo(() => {
    if (!_.isNil(data) && !_.isEmpty(data)) {
      return data;
    } else {
      return [];
    }
  }, [data]);

  useEffect(() => {
    setRows(originalRows);
  }, [originalRows]);

  const [rows, setRows] = useState([]);
  const [searchVal, setSearchVal] = useState("");

  const { sortBy, sortOrder, requestSort, sortData } = useTableSort();

  const createSortHandler = (property) => (event) => {
    requestSort(event, property);
  };

  const handleRefresh = () => {
    setSearchVal("");
    refreshDetailList();
  };

  const requestSearch = (e) => {
    const searchedVal = e.target.value;
    setSearchVal(searchedVal);
    const filteredRows = originalRows.filter((row) => {
      return Object.values(row).some((r) =>
        r.toString().toLowerCase().includes(searchedVal.toLowerCase())
      );
    });
    setRows(filteredRows);
  };

  const cancelSearch = () => {
    setSearchVal("");
    setRows(originalRows);
  };

  if (columns.length === 0 || originalRows.length === 0) {
    return null;
  }

  return (
    <Paper className={classes.root}>
      <div className={classes.toolbarContainer}>
        <SearchBar
          kpi={kpi}
          value={searchVal}
          handleChange={(e) => requestSearch(e)}
          onCancelSearch={() => cancelSearch()}
          filteredItemsCount={rows ? rows.length : 0}
          totalItemsCount={originalRows ? originalRows.length : 0}
        />
        <ExportExcelButton
          data={rows}
          columns={columns}
          fileName={kpi?.nombre_kpi || "KPI Detalle"}
        />
        <IconButton onClick={handleRefresh}>
          <Icon className="fas fa-sync" style={commonIcon} />
        </IconButton>
      </div>
      <TableContainer className={classes.container}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <StyledTableCell
                  key={column.colName}
                  variant="head"
                  align={column.align || "inherit"}
                >
                  <TableSortLabel
                    active={sortBy === column.colName}
                    direction={sortBy === column.colName ? sortOrder : "asc"}
                    onClick={createSortHandler(column.colName)}
                  >
                    {column.colName}
                  </TableSortLabel>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortData(rows).map((row) => {
              return (
                <StyledTableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row?.Fila}
                >
                  {columns.map((column) => {
                    const type = column?.type;
                    const baseValue = row?.[column?.colName];
                    const value =
                      type === "datetime"
                        ? safeFormattingDate(
                            DATE_TIME_PATTERN,
                            FRONT_DATE_TIME_PATTERN,
                            baseValue
                          )
                        : baseValue;

                    return (
                      <TableCell
                        key={column.colName}
                        align={column.align || "inherit"}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
